'use client';

import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import Link from '../components/SafeLink';
import { useRouter } from 'next/navigation';

const MobileMenu = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const [isServicesOpen, setIsServicesOpen] = useState(false);
    const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
    const [isAboutOpen, setIsAboutOpen] = useState(false);

    const handleLinkClick = () => {
        setIsServicesOpen(false);
        setIsSolutionsOpen(false);
        setIsAboutOpen(false);
        onClose(); 
    };

    if (!isOpen) return null;

    const arrowDownSvg = (
        <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
    );
    const arrowUpSvg = (
        <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path></svg>
    );
    const arrowRightSvg = (
        <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
    );

    return (
        <div className="fixed inset-0 bg-white z-50 flex flex-col pt-24 px-6 md:hidden overflow-y-auto">
            <button onClick={onClose} className="absolute top-6 right-6">
                <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
            </button>
            <div className="flex flex-col space-y-4">
                <div className="py-4 border-b border-gray-200">
                    <button
                        onClick={() => setIsServicesOpen(prev => !prev)}
                        className="flex justify-between items-center w-full font-bold text-2xl text-blue-900"
                    >
                        Услуги{isServicesOpen ? arrowUpSvg : arrowDownSvg}
                    </button>
                    {isServicesOpen && (
                        <div className="flex flex-col space-y-2 mt-4 p-2 bg-[#B9F5D2] rounded-lg">
                            <Link href="/services/recruitment" onClick={handleLinkClick} className="flex justify-between items-center px-4 py-3 rounded-full bg-white text-black border-2 border-[#4CAF50] font-semibold hover:bg-[#4CAF50] hover:text-white transition-colors duration-200">Подбор персонала{arrowRightSvg}</Link>
                            <Link href="/services/temporary-staff" onClick={handleLinkClick} className="flex justify-between items-center px-4 py-3 rounded-full bg-white text-black border-2 border-[#4CAF50] font-semibold hover:bg-[#4CAF50] hover:text-white transition-colors duration-200">Временный персонал{arrowRightSvg}</Link>
                        </div>
                    )}
                </div>
                <div className="py-4 border-b border-gray-200">
                    <button
                        onClick={() => setIsSolutionsOpen(prev => !prev)}
                        className="flex justify-between items-center w-full font-bold text-2xl text-blue-900"
                    >
                        Решения{isSolutionsOpen ? arrowUpSvg : arrowDownSvg}
                    </button>
                    {isSolutionsOpen && (
                        <div className="flex flex-col space-y-2 mt-4 p-2 bg-[#FFEA66] rounded-lg">
                            <Link href="/solutions/for-employers" onClick={handleLinkClick} className="flex justify-between items-center px-4 py-3 rounded-full bg-white text-black border-2 border-[#FFD600] font-semibold hover:bg-[#FFD600] hover:text-black transition-colors duration-200">Для работодателей{arrowRightSvg}</Link>
                        </div>
                    )}
                </div>
                <Link href="/pricing" className="font-bold text-2xl text-blue-900 border-b border-gray-200 py-4" onClick={handleLinkClick}>Цены</Link>
                <Link href="/resources" className="font-bold text-2xl text-blue-900 border-b border-gray-200 py-4" onClick={handleLinkClick}>Ресурсы</Link>
                <div className="py-4 border-b border-gray-200">
                    <button
                        onClick={() => setIsAboutOpen(prev => !prev)}
                        className="flex justify-between items-center w-full font-bold text-2xl text-blue-900"
                    >
                        О нас{isAboutOpen ? arrowUpSvg : arrowDownSvg}
                    </button>
                    {isAboutOpen && (
                        <div className="flex flex-col space-y-2 mt-4 p-2 bg-[#E0CCEE] rounded-lg">
                            <Link href="/about/team" onClick={handleLinkClick} className="flex justify-between items-center px-4 py-3 rounded-full bg-white text-black border-2 border-[#9370DB] font-semibold hover:bg-[#9370DB] hover:text-white transition-colors duration-200">Команда{arrowRightSvg}</Link>
                        </div>
                    )}
                </div>
                <Link href="/contacts" className="font-bold text-2xl text-blue-900 border-b border-gray-200 py-4" onClick={handleLinkClick}>Контакты</Link>
                <Link href="/request" className="font-bold text-xl bg-[#ffd600] text-blue-900 rounded-full px-4 py-2 text-center mt-8" onClick={handleLinkClick}>Оставить заявку</Link>
            </div>
        </div>
    );
};

export default function Header() {
    const router = useRouter();
    const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
    const [isSolutionsDropdownOpen, setIsSolutionsDropdownOpen] = useState(false);
    const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');

    const servicesDropdownRef = useRef<HTMLDivElement>(null);
    const solutionsDropdownRef = useRef<HTMLDivElement>(null);
    const aboutDropdownRef = useRef<HTMLDivElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);

    const closeAllDropdowns = () => {
        setIsServicesDropdownOpen(false);
        setIsSolutionsDropdownOpen(false);
        setIsAboutDropdownOpen(false);
    };

    useEffect(() => {
        const isAnyDropdownOpen = isServicesDropdownOpen || isSolutionsDropdownOpen || isAboutDropdownOpen;

        if (isAnyDropdownOpen) {
            document.body.classList.add('overflow-x-hidden');
        } else {
            document.body.classList.remove('overflow-x-hidden');
        }

        return () => {
            document.body.classList.remove('overflow-x-hidden');
        };
    }, [isServicesDropdownOpen, isSolutionsDropdownOpen, isAboutDropdownOpen]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Node;

            if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(target)) {
                setIsServicesDropdownOpen(false);
            }
            if (solutionsDropdownRef.current && !solutionsDropdownRef.current.contains(target)) {
                setIsSolutionsDropdownOpen(false);
            }
            if (aboutDropdownRef.current && !aboutDropdownRef.current.contains(target)) {
                setIsAboutDropdownOpen(false);
            }

            const targetElement = event.target as HTMLElement;
            if (
                isSearchOpen &&
                !targetElement.closest('.search-toggle-button') &&
                searchContainerRef.current &&
                !searchContainerRef.current.contains(targetElement)
            ) {
                setIsSearchOpen(false);
                setSearchQuery('');
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isServicesDropdownOpen, isSolutionsDropdownOpen, isAboutDropdownOpen, isSearchOpen]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (searchQuery.length > 1) {
            router.push(`/search?q=${searchQuery}`);
            setIsSearchOpen(false);
            setSearchQuery('');
        }
    };

    const dropdownMenuBaseClasses = "absolute left-0 mt-2 w-72 bg-[#B9F5D2] text-blue-900 rounded-md shadow-lg py-4 px-2 z-10";
    const dropdownItemClasses = "flex justify-between items-center px-4 py-3 text-lg font-semibold border-2 border-[#4CAF50] bg-white text-black rounded-full mb-2 hover:bg-[#4CAF50] hover:text-white transition-colors duration-200";

    const solutionsDropdownMenuClasses = "absolute left-0 mt-2 w-72 bg-[#FFEA66] text-black rounded-md shadow-lg py-4 px-2 z-10";
    const solutionsDropdownItemClasses = "flex justify-between items-center px-4 py-3 text-lg font-semibold border-2 border-[#FFD600] bg-white text-black rounded-full mb-2 hover:bg-[#FFD600] hover:text-black transition-colors duration-200";

    const aboutDropdownMenuClasses = "absolute left-0 mt-2 w-72 bg-[#E0CCEE] text-black rounded-md shadow-lg py-4 px-2 z-10";
    const aboutDropdownItemClasses = "flex justify-between items-center px-4 py-3 text-lg font-semibold border-2 border-[#9370DB] bg-white text-black rounded-full mb-2 hover:bg-[#9370DB] hover:text-white transition-colors duration-200";

    const arrowRightSvg = (
        <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
    );
    const arrowDownSvg = (
        <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
    );
    const arrowUpSvg = (
        <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path></svg>
    );
    const goToArrowSvg = (
        <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
    );
    const searchSvg = (
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 21l-4.35-4.35M19 11a8 8 0 11-16 0 8 8 0 0116 0z"></path>
        </svg>
    );
    const closeSvg = (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
    );

    return (
        <>
            <header className="header-bg text-white py-4 md:py-7 fixed top-0 left-0 right-0 z-50">
                <div className="w-full mx-auto px-4 md:px-6 lg:px-12">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4 md:space-x-4">
                            <Link href="/" className="flex items-center" aria-label="На главную страницу">
                                <Image src="/images/interimlogo.png" alt="Логотип" width={48} height={48} className="h-12 w-auto" />
                            </Link>
                            <nav className="hidden md:flex items-center space-x-2 lg:space-x-4">

                                <div className="relative" ref={servicesDropdownRef}>
                                    <Link
                                        href="/services"
                                        className={`font-bold text-base md:text-sm lg:text-xl p-2 rounded-full border-[2px] flex items-center transition-colors duration-200 ${isServicesDropdownOpen ? 'bg-[#B9F5D2] border-[#003087] text-black' : 'border-2 border-transparent hover:border-white text-white'}`}
                                        onClick={(e) => { e.preventDefault(); setIsServicesDropdownOpen(prev => !prev); setIsSolutionsDropdownOpen(false); setIsAboutDropdownOpen(false); }}
                                    >
                                        Услуги{isServicesDropdownOpen ? arrowUpSvg : arrowDownSvg}
                                    </Link>
                                    {isServicesDropdownOpen && (
                                        <div className={dropdownMenuBaseClasses}>
                                            <Link href="/services/recruitment" onClick={closeAllDropdowns} className={dropdownItemClasses}>Подбор персонала{arrowRightSvg}</Link>
                                            <Link href="/services/temporary-staff" onClick={closeAllDropdowns} className={dropdownItemClasses}>Временный персонал{arrowRightSvg}</Link>
                                            <Link href="/services/training-development" onClick={closeAllDropdowns} className={dropdownItemClasses}>Обучение и развитие персонала{arrowRightSvg}</Link>
                                            <Link href="/services/workload-reduction" onClick={closeAllDropdowns} className={dropdownItemClasses}>Снижение кадровой нагрузки{arrowRightSvg}</Link>
                                            <Link href="/services/rare-specialists" onClick={closeAllDropdowns} className={dropdownItemClasses}>Подбор редких специалистов{arrowRightSvg}</Link>
                                            <Link href="/services/hr-consulting" onClick={closeAllDropdowns} className={dropdownItemClasses}>HR‑консалтинг{arrowRightSvg}</Link>
                                            <Link href="/services/outstaffing-functions" onClick={closeAllDropdowns} className={dropdownItemClasses}>Аутстаффинг функций{arrowRightSvg}</Link>
                                            <Link href="/services/outsourcing-functions" onClick={closeAllDropdowns} className={dropdownItemClasses}>Аутсорсинг функций{arrowRightSvg}</Link>
                                            <Link href="/services" onClick={closeAllDropdowns} className="flex items-center px-4 py-2 text-black text-base font-bold mt-4 hover:bg-[#4CAF50] hover:text-white transition-colors duration-200 rounded-full">{goToArrowSvg}Перейти к Услугам</Link>
                                        </div>
                                    )}
                                </div>

                                <div className="relative" ref={solutionsDropdownRef}>
                                    <Link
                                        href="/solutions"
                                        className={`font-bold text-base md:text-sm lg:text-xl p-2 rounded-full border-[2px] flex items-center transition-colors duration-200 ${isSolutionsDropdownOpen ? 'bg-[#FFEA66] border-[#003087] text-black' : 'border-2 border-transparent hover:border-white text-white'}`}
                                        onClick={(e) => { e.preventDefault(); setIsSolutionsDropdownOpen(prev => !prev); setIsServicesDropdownOpen(false); setIsAboutDropdownOpen(false); }}
                                    >
                                        Решения{isSolutionsDropdownOpen ? arrowUpSvg : arrowDownSvg}
                                    </Link>
                                    {isSolutionsDropdownOpen && (
                                        <div className={solutionsDropdownMenuClasses}>
                                            <Link href="/solutions/for-employers" onClick={closeAllDropdowns} className={solutionsDropdownItemClasses}>Для работодателей{arrowRightSvg}</Link>
                                            <Link href="/solutions/for-applicants" onClick={closeAllDropdowns} className={solutionsDropdownItemClasses}>Для соискателей{arrowRightSvg}</Link>
                                            <Link href="/solutions/corporate-partners" onClick={closeAllDropdowns} className={solutionsDropdownItemClasses}>Корпоративным партнёрам{arrowRightSvg}</Link>
                                            <Link href="/solutions" onClick={closeAllDropdowns} className="flex items-center px-4 py-2 text-black text-base font-bold mt-4 hover:bg-[#FFD600] hover:text-black transition-colors duration-200 rounded-full">{goToArrowSvg}Перейти к Решениям</Link>
                                        </div>
                                    )}
                                </div>

                                <Link href="/pricing" className="font-bold text-base md:text-sm lg:text-xl p-2 rounded-full border-2 border-transparent hover:border-white">Цены</Link>
                                <Link href="/resources" className="font-bold text-base md:text-sm lg:text-xl p-2 rounded-full border-2 border-transparent hover:border-white">Ресурсы</Link>
                            </nav>
                        </div>

                        <nav className="hidden md:flex items-center space-x-2 lg:space-x-4">

                            <div className="relative" ref={aboutDropdownRef}>
                                <Link
                                    href="/about"
                                    className={`font-bold text-base md:text-sm lg:text-xl p-2 rounded-full border-[2px] flex items-center transition-colors duration-200 ${isAboutDropdownOpen ? 'bg-[#E0CCEE] border-[#003087] text-black' : 'border-2 border-transparent hover:border-white text-white'}`}
                                    onClick={(e) => { e.preventDefault(); setIsAboutDropdownOpen(prev => !prev); setIsServicesDropdownOpen(false); setIsSolutionsDropdownOpen(false); }}
                                >
                                    О нас{isAboutDropdownOpen ? arrowUpSvg : arrowDownSvg}
                                </Link>
                                {isAboutDropdownOpen && (
                                    <div className={aboutDropdownMenuClasses}>
                                        <Link href="/about/team" onClick={closeAllDropdowns} className={aboutDropdownItemClasses}>Команда{arrowRightSvg}</Link>
                                        <Link href="/about/partners" onClick={closeAllDropdowns} className={aboutDropdownItemClasses}>Партнёры{arrowRightSvg}</Link>
                                        <Link href="/about/career" onClick={closeAllDropdowns} className={aboutDropdownItemClasses}>Карьера{arrowRightSvg}</Link>
                                        <Link href="/about" onClick={closeAllDropdowns} className="flex items-center px-4 py-2 text-black text-base font-bold mt-4 hover:bg-[#9370DB] hover:text-white transition-colors duration-200 rounded-full">{goToArrowSvg}Подробнее о нас</Link>
                                    </div>
                                )}
                            </div>

                            <Link href="/contacts" className="font-bold text-base md:text-sm lg:text-xl p-2 rounded-full border-2 border-transparent hover:border-white">Контакты</Link>

                            <button
                                onClick={() => {
                                    setIsSearchOpen(prev => !prev);
                                    setSearchQuery('');
                                }}
                                className="text-white w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full border-2 border-transparent hover:border-white transition-colors duration-200 search-toggle-button"
                            >
                                {isSearchOpen ? closeSvg : searchSvg}
                            </button>

                            <Link href="/request" className="font-bold text-base md:text-sm lg:text-xl bg-[#ffd600] border-2 border-[#ffd600] text-blue-900 rounded-full px-3 py-2 hover:bg-[#ffea66] transition">Оставить заявку</Link>
                        </nav>

                        <div className="flex items-center space-x-4 md:hidden">
                            <button
                                onClick={() => {
                                    setIsSearchOpen(prev => !prev);
                                    setSearchQuery('');
                                }}
                                className="text-white w-10 h-10 flex items-center justify-center rounded-full border-2 border-transparent hover:border-white transition-colors duration-200 search-toggle-button"
                            >
                                {isSearchOpen ? closeSvg : searchSvg}
                            </button>
                            <button
                                onClick={() => setIsMobileMenuOpen(true)}
                                className="flex items-center p-2 rounded-full hover:bg-gray-700 transition"
                                aria-label="Открыть мобильное меню"
                            >
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {isSearchOpen && (
                <div ref={searchContainerRef} className="fixed top-[64px] md:top-[92px] left-0 right-0 bg-[#003087] z-40 py-6 md:py-10 px-4 border-b border-white border-opacity-30 flex items-center justify-center">
                    <div className="relative w-full max-w-4xl">
                        <form onSubmit={handleSearchSubmit}>
                            <input
                                type="text"
                                placeholder="Что вы ищете?"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-transparent text-white placeholder-gray-400 text-lg md:text-xl py-3 pl-4 pr-16 border-b border-white focus:outline-none"
                            />
                            <button
                                type="submit"
                                className="absolute right-0 top-1/2 -translate-y-1/2 bg-[#123eb7] text-white px-4 md:px-6 py-2 md:py-3 hover:bg-[#3269ff] transition-colors duration-200 flex items-center space-x-2"
                            >
                                {searchSvg}<span>Найти</span>
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        </>
    );
}